import { useMemo, useRef, useState } from "react";

import { createPushDiagnostics } from "./pushDiagnostics";
import type { ShellServices } from "./services";

type DiagnosticsServices = ShellServices & Required<Pick<ShellServices, "notifications">>;

/**
 * Диагностика доставки для случаев, когда журналов сервера не видно.
 *
 * Собирает отчёт с этого устройства и сервера и отправляет проверочное
 * уведомление, наблюдая за доставкой. Текст можно скопировать и переслать: в нём
 * нет паролей, адресов подписок и ключей шифрования. Кнопка нужна затем, что на
 * телефоне консоли браузера нет, а именно телефон чаще всего и проверяют.
 */
export function NotificationDiagnostics({
  services,
  waitSeconds = 30,
}: {
  services: DiagnosticsServices;
  /** Сколько секунд наблюдать за доставкой после проверочного уведомления. */
  waitSeconds?: number;
}) {
  const run = useMemo(() => createPushDiagnostics(services), [services]);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);

  const collect = () => {
    setRunning(true);
    setCopied(false);
    setReport(null);
    void run({ probe: true, waitSeconds })
      .then((result) => setReport(JSON.stringify(result, null, 2)))
      .catch((error: unknown) => setReport(`Не удалось собрать диагностику: ${error instanceof Error ? error.message : String(error)}`))
      .finally(() => setRunning(false));
  };

  const copy = () => {
    area.current?.select();
    void navigator.clipboard
      ?.writeText(report ?? "")
      .then(() => setCopied(true))
      // Буфер обмена может быть закрыт (iPhone вне жеста): текст уже выделен, его можно скопировать вручную.
      .catch(() => setCopied(false));
  };

  return (
    <div className="notification-diagnostics">
      <button className="button button--text button--compact" type="button" disabled={running} onClick={collect}>
        {running ? "Собираем диагностику… до 30 секунд" : "Собрать диагностику доставки"}
      </button>
      <p className="dw-secondary">
        Отправит проверочное уведомление и покажет, где рвётся доставка. В отчёте нет паролей, адресов подписок и ключей
        шифрования.
      </p>
      {report === null ? null : (
        <>
          <textarea ref={area} className="notification-diagnostics__report" readOnly rows={10} value={report} aria-label="Отчёт диагностики" />
          <button className="button button--secondary button--compact" type="button" onClick={copy}>
            {copied ? "Скопировано" : "Скопировать отчёт"}
          </button>
        </>
      )}
    </div>
  );
}
